import { useAutoAnimate } from "@formkit/auto-animate/react";
import { type Breadcrumb, useBreadcrumb } from "components/Breadcrumb";
import { EmailConnectionRequiredBanner } from "components/EmailConnectionRequiredBanner";
import { Loading } from "components/ui/Loading/Loading";
import { useActiveGmailConnection } from "hooks/use-email-integrations-api";
import {
  type ReplyEmailItem,
  type ReplyResponseStatus,
  useReplies,
  useReplyBatch,
  useReplyStats,
  useUpdateReplyStatus,
} from "hooks/use-email-replies-api";
import { Inbox, MailPlus, MessagesSquare } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { toastError } from "utils/toast-utils";

import { BatchActionBar } from "./components/BatchActionBar";
import { CollapsiblePanel } from "./components/CollapsiblePanel";
import { CrmLinkDialog } from "./components/CrmLinkDialog";
import { ReplyComposerColumn } from "./components/ReplyComposerColumn";
import { ReplyDashboardHeader } from "./components/ReplyDashboardHeader";
import {
  ReplyFilterBar,
  type ReplyLabel,
  type ReplyPeriod,
} from "./components/ReplyFilterBar";
import { ReplyListItem } from "./components/ReplyListItem";
import { ReplyThreadColumn } from "./components/ReplyThreadColumn";
import { useColumnVisibility } from "./hooks/use-column-visibility";
import {
  nextLabel,
  useReplyKeyboardShortcuts,
} from "./hooks/use-reply-keyboard-shortcuts";
import { groupRepliesByThread } from "./lib/group-replies-by-thread";

const PERIOD_HOURS: Record<ReplyPeriod, number | null> = {
  all: null,
  "24h": 24,
  "7d": 24 * 7,
  "30d": 24 * 30,
};

const BREAD_CRUMBS: Breadcrumb[] = [
  { label: "ホーム", to: "/" },
  { label: "メール" },
  { label: "返信管理" },
];

/**
 * 返信管理ページ. 常に最大 2 ペイン構成のレイアウト.
 * メールスレッド (②) を常時表示し、返信リスト (①) と返信フォーム (③) は排他で切り替える.
 * 初期は ① + ②、返信リストの項目を選択すると ① が畳まれ ③ が現れる.
 * @return {React.ReactElement} 返信管理画面
 */
export const EmailRepliesPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const { setBreadcrumbItems } = useBreadcrumb();
  const { visibility, showList, showComposer } = useColumnVisibility();
  // ペインの出現/消滅・リスト⇄rail・スレッドのリフローを自動アニメーション
  const [paneContainerRef] = useAutoAnimate<HTMLDivElement>();

  useEffect(() => {
    setBreadcrumbItems(BREAD_CRUMBS);
  }, [setBreadcrumbItems]);

  const status =
    (params.get("status") as ReplyResponseStatus | "all" | null) ?? "all";
  const label = (params.get("label") as ReplyLabel | null) ?? "all";
  const period = (params.get("period") as ReplyPeriod | null) ?? "all";
  const query = params.get("q") ?? "";
  const seriesId = params.get("seriesId");
  const selectedReplyId = params.get("id");

  const setParam = useCallback(
    (key: string, value: string | null) => {
      setParams(prev => {
        const current = prev.get(key);
        const shouldDelete = value === null || value === "" || value === "all";
        if (shouldDelete && current === null) return prev;
        if (!shouldDelete && current === value) return prev;
        const next = new URLSearchParams(prev);
        if (shouldDelete) {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        return next;
      });
    },
    [setParams],
  );

  const setStatus = useCallback(
    (next: ReplyResponseStatus | "all") =>
      setParam("status", next === "all" ? null : next),
    [setParam],
  );
  const setLabel = useCallback(
    (next: ReplyLabel) => setParam("label", next === "all" ? null : next),
    [setParam],
  );
  const setPeriod = useCallback(
    (next: ReplyPeriod) => setParam("period", next === "all" ? null : next),
    [setParam],
  );
  const setQuery = useCallback(
    (next: string) => setParam("q", next === "" ? null : next),
    [setParam],
  );
  const setSeriesId = useCallback(
    (next: string | null) => setParam("seriesId", next),
    [setParam],
  );
  const setSelectedId = useCallback(
    (id: string | null) => setParam("id", id),
    [setParam],
  );

  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [crmDialogReply, setCrmDialogReply] = useState<ReplyEmailItem | null>(
    null,
  );

  const focusSearchInput = useCallback(() => {
    const el = document.querySelector<HTMLInputElement>(
      "[data-reply-search-input]",
    );
    el?.focus();
    el?.select();
  }, []);

  const { stats, refetch: refetchStats } = useReplyStats();
  const { hasActiveConnection, loading: connectionLoading } =
    useActiveGmailConnection();
  const filters = useMemo(
    () => ({
      status: status === "all" ? null : status,
      seriesId,
      page: 1,
      limit: 100,
    }),
    [status, seriesId],
  );
  const {
    items: rawItems,
    loading,
    error: repliesError,
    refetch,
  } = useReplies(filters);
  const { updateStatus } = useUpdateReplyStatus();
  const { batch } = useReplyBatch();

  // client-side フィルター: ラベル / 期間 / 全文検索
  const items = useMemo(() => {
    const periodHours = PERIOD_HOURS[period];
    const since = periodHours ? Date.now() - periodHours * 3600 * 1000 : null;
    const lowered = query.trim().toLowerCase();
    return rawItems.filter(r => {
      if (label !== "all" && r.parentType !== label) return false;
      if (since !== null) {
        const t = new Date(r.receivedAt).getTime();
        if (Number.isFinite(t) && t < since) return false;
      }
      if (lowered) {
        const haystack = [r.fromEmail, r.fromName, r.subject, r.bodyText]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(lowered)) return false;
      }
      return true;
    });
  }, [rawItems, label, period, query]);

  // スレッド単位グルーピング: 1 thread = 1 行 (Gmail 風)
  const threadGroups = useMemo(() => groupRepliesByThread(items), [items]);

  const groupLatestReplies = useMemo<ReplyEmailItem[]>(
    () => threadGroups.map(g => g.latest),
    [threadGroups],
  );

  const selectedReply = useMemo<ReplyEmailItem | null>(() => {
    if (!selectedReplyId) return null;
    const group = threadGroups.find(g =>
      g.replies.some(r => r.id === selectedReplyId),
    );
    return group?.latest ?? null;
  }, [threadGroups, selectedReplyId]);

  // items が変わって selectedReplyId が一覧に居なくなったら選択解除
  useEffect(() => {
    if (
      selectedReplyId &&
      !threadGroups.some(g => g.replies.some(r => r.id === selectedReplyId))
    ) {
      setSelectedId(null);
    }
  }, [threadGroups, selectedReplyId, setSelectedId]);

  const toggleChecked = useCallback((id: string) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setCheckedIds(new Set()), []);

  /**
   * 1 件のステータスを変更する
   * @param {string} replyId Reply ID
   * @param {ReplyResponseStatus} statusNext 新しいステータス
   * @return {Promise<void>}
   */
  const handleSetStatus = useCallback(
    async (replyId: string, statusNext: ReplyResponseStatus) => {
      try {
        await updateStatus(replyId, statusNext);
        toast.success("ステータスを更新しました");
        await Promise.all([refetch(), refetchStats()]);
      } catch {
        toastError("ステータスの更新に失敗しました");
      }
    },
    [updateStatus, refetch, refetchStats],
  );

  /**
   * 選択した複数返信を一括更新する
   * @param {"mark_completed"|"mark_in_progress"|"mark_unresponded"} action アクション
   * @return {Promise<void>}
   */
  const handleBatch = useCallback(
    async (
      action: "mark_completed" | "mark_in_progress" | "mark_unresponded",
    ) => {
      if (checkedIds.size === 0) return;
      try {
        const updated = await batch(Array.from(checkedIds), action);
        toast.success(`${updated}件を更新しました`);
        setCheckedIds(new Set());
        await Promise.all([refetch(), refetchStats()]);
      } catch {
        toastError("一括更新に失敗しました");
      }
    },
    [batch, checkedIds, refetch, refetchStats],
  );

  /**
   * 返信フォームを表示する (返信リストを畳む). `r` ショートカット用.
   * @return {void}
   */
  const handleStartReply = useCallback(() => {
    showComposer();
  }, [showComposer]);

  /**
   * 返信リストの項目を選択する. 選択と同時に返信リストを畳み、
   * 返信フォームを表示する (常に最大 2 ペイン構成).
   * @param {string} id 選択した返信 (スレッド最新) の ID
   * @return {void}
   */
  const handleSelectReply = useCallback(
    (id: string) => {
      setSelectedId(id);
      showComposer();
    },
    [setSelectedId, showComposer],
  );

  /**
   * 返信送信が成功したときの追従処理
   * @return {void}
   */
  const handleReplySent = useCallback(() => {
    refetch();
    refetchStats();
  }, [refetch, refetchStats]);

  useReplyKeyboardShortcuts({
    items: groupLatestReplies,
    selectedId: selectedReplyId,
    setSelectedId,
    toggleChecked,
    onSetStatusCurrent: statusNext => {
      if (selectedReplyId) handleSetStatus(selectedReplyId, statusNext);
    },
    onStartReply: handleStartReply,
    onMoveLabel: dir => setLabel(nextLabel(label, dir)),
    onFocusSearch: focusSearchInput,
  });

  const totalCount = threadGroups.length;
  const allChecked = checkedIds.size > 0 && checkedIds.size === totalCount;

  return (
    <div className="flex flex-col h-[calc(100vh-60px)] p-3 gap-3">
      <ReplyDashboardHeader stats={stats} />
      {!connectionLoading && !hasActiveConnection && (
        <EmailConnectionRequiredBanner />
      )}
      {repliesError && (
        <div
          className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg px-4 py-3 text-sm flex items-start justify-between gap-3"
          role="alert"
        >
          <div>
            <div className="font-semibold">
              リプライ一覧の取得に失敗しました
            </div>
            <div className="text-xs text-rose-600 mt-0.5">
              {repliesError.message ??
                "サーバーでエラーが発生しました。 時間をおいて再試行してください。"}
            </div>
          </div>
          <button
            type="button"
            onClick={() => refetch()}
            className="text-xs font-semibold text-rose-700 hover:text-rose-900 hover:underline shrink-0"
          >
            再読み込み
          </button>
        </div>
      )}
      <ReplyFilterBar
        status={status}
        label={label}
        period={period}
        query={query}
        seriesId={seriesId}
        stats={stats}
        onStatusChange={setStatus}
        onLabelChange={setLabel}
        onPeriodChange={setPeriod}
        onQueryChange={setQuery}
        onSeriesChange={setSeriesId}
      />

      <div
        ref={paneContainerRef}
        className="flex-1 min-h-0 flex gap-3 overflow-hidden"
      >
        <CollapsiblePanel
          title="返信リスト"
          icon={<Inbox size={14} className="text-slate-500" aria-hidden />}
          collapsed={!visibility.list}
          onToggle={showList}
          collapsible={false}
          widthClassName="basis-[34%] min-w-[300px] max-w-[460px]"
        >
          <div className="h-full flex flex-col min-h-0">
            <BatchActionBar
              checkedCount={checkedIds.size}
              totalCount={totalCount}
              onToggleAll={() => {
                if (allChecked) {
                  setCheckedIds(new Set());
                } else {
                  setCheckedIds(new Set(threadGroups.map(g => g.latest.id)));
                }
              }}
              onClearSelection={clearSelection}
              onBatch={handleBatch}
            />
            {loading ? (
              <div className="flex justify-center py-8">
                <Loading />
              </div>
            ) : threadGroups.length === 0 ? (
              <div className="flex-1 flex items-center justify-center text-sm text-base-content/60">
                該当する返信はありません
              </div>
            ) : (
              <ul className="flex-1 overflow-auto">
                {threadGroups.map(group => (
                  <ReplyListItem
                    key={group.key}
                    reply={group.latest}
                    threadCount={group.count}
                    originLabel={group.originLabel}
                    aggregateStatus={group.aggregateStatus}
                    selected={group.replies.some(r => r.id === selectedReplyId)}
                    checked={checkedIds.has(group.latest.id)}
                    onSelect={handleSelectReply}
                    onToggleChecked={toggleChecked}
                    onChangeStatus={handleSetStatus}
                  />
                ))}
              </ul>
            )}
          </div>
        </CollapsiblePanel>

        <CollapsiblePanel
          title="メールスレッド"
          icon={
            <MessagesSquare size={14} className="text-slate-500" aria-hidden />
          }
          collapsed={false}
          onToggle={showList}
          collapsible={false}
        >
          <ReplyThreadColumn
            reply={selectedReply}
            onSetStatus={handleSetStatus}
            onOpenCrmLink={() => setCrmDialogReply(selectedReply)}
          />
        </CollapsiblePanel>

        {visibility.composer && (
          <CollapsiblePanel
            title="メール返信フォーム"
            icon={<MailPlus size={14} className="text-slate-500" aria-hidden />}
            collapsed={false}
            onToggle={showList}
            widthClassName="basis-[36%] min-w-[340px]"
          >
            <ReplyComposerColumn
              reply={selectedReply}
              canCompose={hasActiveConnection}
              onSent={handleReplySent}
            />
          </CollapsiblePanel>
        )}
      </div>

      <CrmLinkDialog
        isOpen={crmDialogReply !== null}
        reply={crmDialogReply}
        onClose={() => setCrmDialogReply(null)}
        onLinked={() => {
          refetch();
          setCrmDialogReply(null);
        }}
      />
    </div>
  );
};
